import './SprayChart.scss';

import { SprayDto, SprayPoint } from 'types/CodegenReportTypes';
import Legend, { LegendItem } from 'components/Charts/Legend/Legend';
import React, { useCallback, useMemo } from 'react';

import { ReactComponent as BatterSprayChartGraphic } from 'svg/tileGraphics/BatterSprayChart.svg';
import { playResultToColor } from 'utilities/colorHelpers';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import { meterToFeet, playResults } from 'utilities/constants';
import { UnitSystem } from 'types/StartupTypes';

const playResultLegendItems: LegendItem[] = playResults.map((playResult) => ({
  label: playResult,
  dotProps: { color: playResultToColor[playResult], borderRadius: '1px' },
}));

const chartViewboxHeight = 315;
const chartViewboxWidth = 316;
const batterPositionX = 0.5 * chartViewboxWidth;
const batterPositionY = chartViewboxHeight - 37;
const dataToSvgScaleFactorX = 0.46;
const dataToSvgScaleFactorY = 0.46;

function generateRandomPoints(options?: { xMin?: number; xMax?: number; yMin?: number; yMax?: number }): SprayPoint[] {
  const points: SprayPoint[] = [];
  const lerp = (start: number, end: number, t: number): number => start + (end - start) * t;

  const xMin = options?.xMin ?? -275;
  const xMax = options?.xMax ?? 275;
  const yMin = options?.yMin ?? 150;
  const yMax = options?.yMax ?? 500;

  for (let i = 0; i < 10; i++) {
    points.push({ x: lerp(xMin, xMax, Math.random()), y: lerp(yMin, yMax, Math.random()) });
  }

  return points;
}

const SprayPointMarker: React.FC<{ point: SprayPoint; color: string; size: number; unitSystem: UnitSystem }> = ({
  point,
  color,
  size,
  unitSystem,
}) => {
  const positionX = batterPositionX + point.x * dataToSvgScaleFactorX * (unitSystem === 'Metric' ? meterToFeet : 1);
  const positionY = batterPositionY - point.y * dataToSvgScaleFactorY * (unitSystem === 'Metric' ? meterToFeet : 1);
  const offset = size / 2;

  return (
    <rect
      x={positionX - offset}
      y={positionY - offset}
      width={size}
      height={size}
      stroke={color}
      strokeWidth={1}
      fill={`${color}80`}
      rx={1}
    />
  );
};

type Props = {
  sprays: SprayDto[];
  isLoading: boolean;
};

const SprayChart = ({ sprays, isLoading }: Props) => {
  const markerSize = 10;

  const simulateChartData = useCallback((isLoading: boolean) => {
    return isLoading
      ? [
          {
            playResult: 'Single',
            sprayPoints: generateRandomPoints({ yMax: 250 }),
          },
          {
            playResult: 'Double',
            sprayPoints: generateRandomPoints({ yMax: 300 }),
          },
          {
            playResult: 'Triple',
            sprayPoints: generateRandomPoints({ yMin: 300, yMax: 400 }),
          },
          {
            playResult: 'HomeRun',
            sprayPoints: generateRandomPoints({ yMin: 400, yMax: 500 }),
          },
          {
            playResult: 'Out',
            sprayPoints: generateRandomPoints({ yMax: 350 }),
          },
        ]
      : [];
  }, []);

  const chartDataDisplay = useChartDataDisplay(sprays, simulateChartData, isLoading);

  const unitSystem = useMemo(() => (isLoading ? 'Imperial' : useGetUnitSystem()), [isLoading]);

  const filteredLegendItems = playResultLegendItems.filter((item) => chartDataDisplay.some((spray) => spray.playResult === item.label));

  return (
    <div className='SprayChart-container'>
      <div className='SprayChart-chart-wrapper'>
        <BatterSprayChartGraphic className='SprayChart-chart' />
        <svg className={`SprayChart-markers ${isLoading ? 'loading' : ''}`} viewBox={`0 0 ${chartViewboxWidth} ${chartViewboxHeight}`}>
          {chartDataDisplay.map((sprayData, index) => (
            <React.Fragment key={index}>
              {sprayData.sprayPoints.map((point, pointIndex) => (
                <SprayPointMarker
                  key={`${index}-${pointIndex}`}
                  point={point}
                  color={playResultToColor[sprayData.playResult]}
                  size={markerSize}
                  unitSystem={unitSystem}
                />
              ))}
            </React.Fragment>
          ))}
        </svg>
      </div>
      <Legend items={filteredLegendItems} disableSorting={true} />
    </div>
  );
};

export default SprayChart;
